/* global dataLayer */
import { LoginStatus, LoginMethod, PaymentMethod, Currency, CTAType, YES, NO, Page } from '@/common/constants/analytics';
import { hasLaborItem, hasOtherItem, isLaborItem, isOtherItem } from '@/common/utilities/quote';
import appContext from '../appContext';
import { getUTCTimeMilliseconds } from './utilities';

window.dataLayer = window.dataLayer || [];

const productPositions = {};
let positionCount = 0;

export const getTransactionId = () => {
    // Generate stable session-based transaction ID
    if (!sessionStorage.getItem('analyticsTransactionId')) {
        sessionStorage.setItem('analyticsTransactionId', crypto.randomUUID().toString());
    }
    return sessionStorage.getItem('analyticsTransactionId');
};

const getItemNumber = (item) => item.itemNumber ?? item.displayItemNumber;

const createItemId = (item) => `${item.line}_${getItemNumber(item)}`;

const buildItem = (item) => ({
    item_id: createItemId(item),
    item_name: item.itemDescription,
    item_brand: item.line,
    item_category: item.analyticsCategory,
    price: item.itemCost,
    quantity: item.itemQuantity,
});

const buildOrderedItemsList = (quoteItems = []) =>
    quoteItems.map((item) => ({
        ...buildItem(item),
        labor_value: isLaborItem(item) ? item.customerPrice : 0,
        other_value: isOtherItem(item) ? item.customerPrice : 0,
    }));

const getCurrentPath = () => window.location.href.replace(window.location.origin + appContext, '');

function pushComplete(redirectUrl, callback) {
    if (callback) {
        callback();
    }
    if (redirectUrl) {
        window.location.href = redirectUrl;
    }
}

function pushData(data, redirectUrl, callback) {
    const eventTimeoutMs = 1000;
    let hasFired = false;
    let fallbackTimer;

    const completeOnce = () => {
        if (hasFired) return;
        hasFired = true;
        if (fallbackTimer) clearTimeout(fallbackTimer);
        pushComplete(redirectUrl, callback);
    };

    if (redirectUrl || callback) {
        data.eventTimeout = eventTimeoutMs;

        fallbackTimer = setTimeout(() => {
            completeOnce();
        }, eventTimeoutMs);

        data.eventCallback = (id) => {
            // ublock origin replaces GTM script and triggers eventCallback with no parameter
            if (typeof id !== 'string' || id.startsWith('GTM-')) {
                completeOnce();
            }
        };
    }

    // Set global language property, but don't overwrite language_toggle_click event's language parameter
    if (data.event !== 'language_toggle_click') {
        data.language = 'English';
    }

    try {
        dataLayer.push(data);
    } catch (err) {
        completeOnce();
    }
}

function buildProductFieldObject(worksheetDetail) {
    return {
        id: worksheetDetail.line + worksheetDetail.itemNumber,
        name: worksheetDetail.itemDescription,
        category: worksheetDetail.analyticsCategory,
        brand: worksheetDetail.line ? worksheetDetail.line : '',
        price: worksheetDetail.itemCost,
        quantity: worksheetDetail.itemQuantity,
    };
}

function buildProductFieldObjectList(quoteItems) {
    const productList = [];
    for (const prop in quoteItems) {
        if (quoteItems.hasOwnProperty(prop)) {
            productList.push(buildProductFieldObject(quoteItems[prop]));
        }
    }
    return productList;
}

function buildProductImpressionObj(cp, category) {
    const id = cp.catalogKey.groupId + cp.catalogKey.itemId;

    if (productPositions[id] === undefined) {
        positionCount += 1;
        productPositions[id] = positionCount;
    }

    return {
        id,
        name: cp.itemDescription,
        list: cp.productOrigin,
        brand: cp.catalogKey.groupId,
        category,
        position: productPositions[id],
        price: cp.itemCost,
    };
}

function buildProductDetailObj(cp, partTypeStr) {
    const id = cp.catalogKey.groupId + cp.catalogKey.itemId;
    return {
        id,
        name: cp.itemDescription,
        brand: cp.catalogKey.groupId,
        category: partTypeStr,
        price: cp.itemCost,
    };
}

/* expose this function so that we can queue analytics event without analytics being loaded yet */
export function searchAction(options) {
    const { usedTypeAhead, query, accountId } = options.data;

    pushData(
        {
            event: 'search',
            eventCategory: 'Search Performed',
            eventLabel: query,
            eventAction: usedTypeAhead ? 'with typeahead' : 'without typeahead',
            accountId,
        },
        null,
        options.callback
    );
}

/* expose this function so that we can queue analytics event without analytics being loaded yet */
export function searchResultsAction(options) {
    const { resultsRetrieved, query, isPlp, accountId } = options.data;
    let eventAction = 'PLP';

    if (!isPlp) {
        eventAction = resultsRetrieved ? 'Part Type Results' : 'No Results';
    }

    pushData(
        {
            event: 'search_results',
            eventCategory: 'Search Results',
            eventLabel: query,
            eventAction,
            accountId,
        },
        null,
        options.callback
    );
}

/* expose this function so that we can queue analytics event without analytics being loaded yet */
export function currentStoreAction(options) {
    const { currentStoreId } = options.data;

    pushData(
        {
            event: 'current_store',
            eventCategory: 'Current Store',
            eventAction: 'Current Store',
            eventLabel: currentStoreId,
        },
        options.redirect,
        options.callback
    );
}

export const trackPromotionClicked = (promotionDetails) => {
    const { name, promotionType } = promotionDetails;

    pushData({
        event: 'promotion_click',
        promotion_name: name,
        promotion_type: promotionType,
        user_engaged_with_promotions: true,
    });
};

export const GTM = {
    Action: {
        ADD_GLOBAL_DATA: 'addGlobalData',
        VEHICLE_SELECTOR: 'vehicleSelector',
        SEARCH: 'search',
        VEHICLE_SELECTION: 'vehicleSelection',
        VEHICLE_SPECS_CLICK: 'vehicleSpecsClick',
        PURCHASE: 'purchase',
        VIDEO_ENGAGEMENT_CLICK: 'videoEngagementClick',
        LOGIN: 'login',
        BEGIN_CHECKOUT: 'beginCheckout',
        ADD_TO_CART: 'addToCart',
        ACCOUNT_INFO_VIEW: 'accountInfoView',
        SEARCH_PERFORMED: 'searchPerformed',
        VIEW_ITEM: 'viewItem',
        LABOR_ADDED: 'laborAdded',
        LABOR_SHOWN: 'showLaborForm',
        SORT_APPLIED: 'sortApplied',
        FILTER_APPLIED: 'filterApplied',
        LANGUAGE_TOGGLE_CLICK: 'languageToggleClick',
        VIEW_PLP: 'viewPLP',
    },
};

const getLaborTypeLabel = (isLaborGuide) => (isLaborGuide ? 'Labor Guide' : 'Custom');

const laborEvent = (eventAction, eventLabel) =>
    pushData({
        event: 'fco_gtm',
        eventCategory: 'Labor',
        eventAction,
        eventLabel,
    });

const actions = {
    addGlobalData(options) {
        pushData({ ...options.data });
    },
    addToCart(options) {
        pushData({ event: 'add_to_cart', ...options.data });
    },
    beginCheckout(options = { data: {} }) {
        pushData({ event: 'begin_checkout', ...options.data });
    },
    vehicleSpecsClick(options = { data: { from: '' } }) {
        pushData({ event: 'vehicle_specs_click', ...options.data });
    },
    videoEngagementClick(options) {
        pushData({ event: 'video_engagement_click', ...options.data });
    },
    login(options) {
        const { loginMethod, loginStatus, userId, userEmail } = options.data;
        const utcTime = getUTCTimeMilliseconds(Date.now());
        const date = new Date(utcTime);

        pushData({
            event: 'user_login',
            login_method: loginMethod,
            login_status: loginStatus,
            login_time: date.toISOString(),
            user_id: userId,
            user_email: userEmail,
        });
    },
    // enhanced ecommerce actions
    purchase(
        options = {
            data: { vehicleCount: 0, headerId: 0, cash: false, costSubtotal: 0, taxSubtotal: 0, quoteItems: [], from: '' },
            redirectUrl: '',
            callback: () => {},
        }
    ) {
        const { data, redirectUrl, callback } = options;
        const trackedData = {
            event: 'purchase',
            vehicle_count: data.vehicleCount,
            includes_labor: hasLaborItem(data.quoteItems),
            includes_other: hasOtherItem(data.quoteItems),
            from: data.from || '',
            ecommerce: {
                currency: Currency.USD,
                transaction_id: data.headerId,
                payment_method: data.isCash ? PaymentMethod.CASH : PaymentMethod.CREDIT_CARD,
                value: data.costSubtotal,
                tax: data.taxSubtotal,
                items: buildOrderedItemsList(data.quoteItems),
            },
        };

        pushData(trackedData, redirectUrl, callback);
    },
    removeFromCart(options) {
        pushData({
            event: 'removeFromCart',
            ecommerce: {
                currencyCode: 'USD',
                remove: {
                    products: [options.data],
                },
            },
        });
    },
    productImpressions(options) {
        const productList = options.data || [];
        const impressions = [];
        let category;

        productList.forEach((partType) => {
            partType.completeProducts?.forEach((cp) => {
                if (cp.productOrigin === 'CATALOG') {
                    category = partType.partTypeName;
                } else {
                    category = cp.productOrigin;
                }
                impressions.push(buildProductImpressionObj(cp, category));
            });
        });
        const data = {
            event: 'productImpressions',
            ecommerce: {
                impressions,
            },
        };
        pushData(data);
    },
    productClick(options) {
        const gaProduct = buildProductImpressionObj(options.data, options.category);
        const data = {
            event: 'productClick',
            ecommerce: {
                products: [gaProduct],
            },
        };
        pushData(data, options.redirect, options.callback);
    },
    productDetail(options) {
        const p = buildProductDetailObj(options.data, options.category);
        const data = {
            event: 'productDetail',
            ecommerce: {
                detail: {
                    products: [p],
                },
            },
        };

        window.addEventListener('load', () => {
            pushData(data);
        });
    },
    checkout(options) {
        const quoteData = options.data;
        const data = {
            event: 'checkout',
            ecommerce: {
                checkout: {
                    products: buildProductFieldObjectList(quoteData.quoteItems),
                },
            },
        };

        // we need to wait for the page/resources to load so that the window property
        // google_tag_manager is defined.
        window.addEventListener('load', () => {
            pushData(data);
        });
    },

    // end enhanced ecommerce actions
    catalogLookup(options) {
        const productTypes = options.data;
        const productTypesLength = productTypes.length;
        const lastProductType = productTypesLength - 1;
        let callback = null;

        if (productTypesLength < 1) {
            options.callback();
            return;
        }
        for (let i = 0; i < productTypesLength; i++) {
            if (i === lastProductType) {
                ({ callback } = options);
            }
            pushData(
                {
                    event: 'fco_gtm',
                    eventCategory: 'catalogLookup',
                    eventAction: productTypes[i].gaTabOrigin,

                    // package jobs don't have partTypeName, so it's been approved to just set a string of package jobs
                    eventLabel: productTypes[i].partTypeName || productTypes[i].gaTabOrigin,
                    partTypeId: productTypes[i].partTypeId,
                },
                null,
                callback
            );
        }
    },
    vehicleSelection(options) {
        pushData(
            {
                event: 'Vehicle Select',
                eventCategory: 'Vehicle Selection',
                eventAction: options.data,
                eventLabel: getCurrentPath(),
            },
            options.redirect,
            options.callback
        );
    },
    viewPLP({ data: { transactionId, searchId, searchTerm, partTypes, method, vehicleAdded, searchTechnology, disposition, duration } }) {
        const payload = {
            event: 'view_plp',
        };

        if (transactionId) payload.transactionId = transactionId;
        if (searchId) payload.searchId = searchId;
        if (searchTerm) payload.searchTerm = searchTerm;
        if (partTypes) payload.partTypes = partTypes;
        if (method) payload.method = method;
        if (typeof vehicleAdded === 'boolean') payload.vehicleAdded = vehicleAdded;
        if (searchTechnology) payload.searchTechnology = searchTechnology;
        if (disposition) payload.disposition = disposition;
        if (duration || duration === 0) payload.duration = duration;

        pushData(payload);
    },
    /* part searching (main searchbar) */
    searchPerformed(options) {
        pushData({
            event: 'search_performed',
            ...options.data,
        });
    },
    search: searchAction,
    searchResults: searchResultsAction,
    currentStore: currentStoreAction,
    /* vehicle selector actions (vin contacted, option selected, etc) */
    vehicleSelector(options) {
        pushData({
            event: 'fco_gtm',
            eventCategory: 'Vehicle Selection',
            eventAction: options.data.eventAction,
        });
    },

    quotesAndOrders(options) {
        pushData(
            {
                event: 'fco_gtm',
                eventCategory: 'quotesAndOrders',
                eventAction: options.action,
                startDate: options.data.startDate,
                eventLabel: options.data.startDate,
            },
            options.redirect,
            options.callback
        );
    },
    home(options) {
        pushData({
            event: 'fco_gtm',
            eventCategory: 'orderRedirect',
            eventAction: options.data.isOrderRedirect,
            eventLabel: 'home',
        });
    },
    manageCustomers(options) {
        pushData({
            event: 'fco_gtm',
            eventCategory: 'manageCustomers',
            eventAction: options.data,
            eventLabel: getCurrentPath(),
        });
    },
    plpSort(options) {
        pushData({
            event: 'fco_gtm',
            eventCategory: 'PLP Sort',
            eventAction: options.action,
            eventLabel: getCurrentPath(),
        });
    },
    plpTableFilter() {
        pushData({
            event: 'tableFilteredPLP',
            eventCategory: 'Table Filtered',
            eventAction: 'tableFilteredPLP',
            eventLabel: getCurrentPath(),
        });
    },
    showLaborForm({ data: { isLaborGuide } }) {
        laborEvent('Show Form', getLaborTypeLabel(isLaborGuide));
    },
    laborAdded(options) {
        pushData({
            event: 'labor_added',
            ...options.data,
        });
    },
    ctaClick({ data: { linkText, linkUrl, ctaLocation } }) {
        pushData({
            event: 'cta_click',
            linkText,
            linkUrl,
            ctaLocation,
            ctaType: CTAType.BANNER,
        });
    },
    accountInfoView() {
        pushData({ event: 'account_info_view' });
    },
    viewItem(options) {
        pushData({ event: 'view_item', ecommerce: { ...options.data } });
    },
    sortApplied({ data: { sortType, sortDirection, resultsReturn, transactionId } }) {
        pushData({
            event: 'sort_applied',
            sortType,
            sortDirection,
            resultsReturn,
            transactionId,
        });
    },
    filterApplied(options) {
        pushData({
            event: 'filter_applied',
            ...options.data,
        });
    },
    languageToggleClick(options) {
        pushData({
            event: 'language_toggle_click',
            ...options.data,
        });
    },
};

const analytics = function trackGoogleAnalyticsData(options) {
    const { data, event, redirect, callback } = options;

    try {
        if (data && event && actions[event]) {
            actions[event](options);
        } else {
            pushComplete(redirect, callback);
        }
    } catch (error) {
        // in case something goes wrong, continue flow
        pushComplete(redirect, callback);
    }
};

export const trackVehicleSpecsClick = (from) => {
    analytics({ event: GTM.Action.VEHICLE_SPECS_CLICK, data: { from } });
};

export const trackVideoEngagementClick = (data) => {
    analytics({ event: GTM.Action.VIDEO_ENGAGEMENT_CLICK, data });
};

export const trackBeginCheckout = (data) => {
    const items = data.items.map((item) => buildItem(item));

    analytics({
        event: GTM.Action.BEGIN_CHECKOUT,
        data: {
            ecommerce: {
                transaction_id: data.headerId,
                currency: Currency.USD,
                value: data.subtotalCost,
                payment_method: data.cash ? PaymentMethod.CASH : PaymentMethod.CREDIT_CARD,
                items,
            },
        },
    });
};

export const trackUserLogIn = ({ loginSuccessful, userEmail, userId, isSSO }) => {
    analytics({
        event: GTM.Action.LOGIN,
        data: {
            loginStatus: loginSuccessful ? LoginStatus.SUCCESS : LoginStatus.FAILURE,
            loginMethod: isSSO ? LoginMethod.SSO : LoginMethod.EMAIL,
            userEmail,
            userId,
        },
    });
};

export const trackAddToCart = ({ from = '', items = [], worksheetHeaderId = '', cartTotalValue = 0, userCartValue = 0 }) => {
    const trackedItems = items.map((item) => buildItem(item));

    analytics({
        event: GTM.Action.ADD_TO_CART,
        data: {
            user_cart_value: userCartValue,
            ecommerce: {
                from,
                transaction_id: worksheetHeaderId,
                cart_total_value: cartTotalValue,
                items: trackedItems,
            },
        },
    });
};

export const trackUserViewItem = ({
    transactionId,
    searchId,
    pageNumber,
    index,
    method,
    pdpViewedFrom,
    vehicleAdded,
    itemCost,
    item = {
        item_id: '',
        item_name: '',
        item_brand: '',
        item_category: '',
        price: 0,
    },
}) => {
    // Ensure transaction_id always has a value
    const finalTransactionId = transactionId || getTransactionId();

    // Ensure method always has a value, use 'Direct' as default for direct navigation
    const finalMethod = method && method !== '' ? method : 'Direct';

    analytics({
        event: GTM.Action.VIEW_ITEM,
        data: {
            transaction_id: finalTransactionId,
            search_id: searchId,
            page_number: pageNumber,
            index,
            method: finalMethod,
            currency: Currency.USD,
            vehicle_added: vehicleAdded ? YES : NO,
            value: itemCost,
            from: pdpViewedFrom ?? Page.DIRECT,
            items: [item],
        },
    });
};

export const trackPLPSort = (fieldName, direction, resultCount, transactionId) => {
    analytics({
        event: GTM.Action.SORT_APPLIED,
        data: { sortType: fieldName, sortDirection: direction, resultsReturn: resultCount, transactionId },
    });
};

export const trackSearchPerformed = (options) => {
    analytics({
        event: GTM.Action.SEARCH_PERFORMED,
        data: { transaction_id: getTransactionId(), ...options },
    });
};

export const trackVehicleAdded = (method, transactionId, from) => {
    pushData({
        event: 'vehicle_added',
        method,
        transaction_id: transactionId,
        from,
    });
};

export const addGlobalData = (options) => {
    analytics({
        event: GTM.Action.ADD_GLOBAL_DATA,
        data: options,
    });
};

export const trackFilterApplied = ({ filterType, filterGroup, resultsReturn, transactionId }) => {
    analytics({
        event: GTM.Action.FILTER_APPLIED,
        data: {
            filter_type: filterType,
            filter_group: Array.isArray(filterGroup) ? filterGroup.join(', ') : filterGroup,
            results_return: resultsReturn,
            transaction_id: transactionId,
        },
    });
};

export const trackLanguageToggleClick = ({ language, from, userLanguage }) => {
    analytics({
        event: GTM.Action.LANGUAGE_TOGGLE_CLICK,
        data: {
            language,
            from,
            user_language: userLanguage,
        },
    });
};

export const trackViewPLP = (data) => {
    analytics({
        event: GTM.Action.VIEW_PLP,
        data,
    });
};

export default analytics;
